import React from "react"
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ListItem,
  OrderedList,
  UnorderedList,
  Divider,
  VStack,
  Container,
} from "@chakra-ui/react"

const MethodologyContent25 = () => {
  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={6} align="stretch">
        <Text fontSize="lg">
          <strong>Objective</strong>: To honor physicians and hospitals for
          their healthcare research productivity during{" "}
          <strong>2023-2024</strong>.
        </Text>

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Step 1: Identify Physicians
          </Heading>
          <Text mb={4}>
            Physicians are identified using the <strong>2024</strong> National
            Plan and Provider Enumeration System (NPPES) file published by the
            Centers for Medicare and Medicaid Services (CMS). Physicians are
            attributed to a specialty according to the table below:
          </Text>

          <Box overflowX="auto">
            <Table variant="striped" colorScheme="gray" size="sm">
              <Thead>
                <Tr>
                  <Th>Specialties</Th>
                  <Th>NPI Primary Specialty Taxonomy</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Orthopedic Surgery</Td>
                  <Td>ORTHOPEDIC SURGERY</Td>
                </Tr>
                <Tr>
                  <Td>Sports Medicine</Td>
                  <Td>SPORTS MEDICINE</Td>
                </Tr>
                <Tr>
                  <Td>Hand Surgery</Td>
                  <Td>HAND SURGERY</Td>
                </Tr>
                <Tr>
                  <Td>Neurosurgery & Spine</Td>
                  <Td>NEUROSURGERY</Td>
                </Tr>
                <Tr>
                  <Td>Cardiothoracic Surgery</Td>
                  <Td>CARDIAC SURGERY, THORACIC SURGERY</Td>
                </Tr>
                <Tr>
                  <Td>Vascular Surgery</Td>
                  <Td>VASCULAR SURGERY</Td>
                </Tr>
                <Tr>
                  <Td>ENT (Otolaryngology)</Td>
                  <Td>OTOLARYNGOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>GI/General Surgery</Td>
                  <Td>GENERAL SURGERY, COLORECTAL SURGERY (PROCTOLOGY)</Td>
                </Tr>
                <Tr>
                  <Td>Urology</Td>
                  <Td>UROLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Surgical Oncology</Td>
                  <Td>SURGICAL ONCOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Ophthalmology</Td>
                  <Td>OPHTHALMOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Gynecologic Surgery</Td>
                  <Td>OBSTETRICS/GYNECOLOGY, GYNECOLOGICAL ONCOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Internal Medicine</Td>
                  <Td>INTERNAL MEDICINE</Td>
                </Tr>
                <Tr>
                  <Td>Cardiology</Td>
                  <Td>CARDIOVASCULAR DISEASE (CARDIOLOGY)</Td>
                </Tr>
                <Tr>
                  <Td>Pulmonology</Td>
                  <Td>PULMONARY DISEASE</Td>
                </Tr>
                <Tr>
                  <Td>Gastroenterology</Td>
                  <Td>GASTROENTEROLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Endocrinology</Td>
                  <Td>ENDOCRINOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Nephrology</Td>
                  <Td>NEPHROLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Neurology</Td>
                  <Td>NEUROLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Hematology</Td>
                  <Td>HEMATOLOGY, HEMATOLOGY/ONCOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Medical Oncology</Td>
                  <Td>MEDICAL ONCOLOGY</Td>
                </Tr>
                <Tr>
                  <Td>Rheumatology</Td>
                  <Td>RHEUMATOLOGY</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Box>

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Step 2: Attribute Physicians to Hospitals
          </Heading>
          <Text mb={4}>
            Medicare surgical volume data is sourced from the 100% Medicare
            fee-for-service (FFS) inpatient and outpatient claims between{" "}
            <strong>2023 and 2024H1</strong>.
          </Text>

          <Heading as="h3" size="base" mb={2}>
            Physician Attribution Criteria
          </Heading>
          <Text mb={4}>
            A physician is attributed to a hospital if the hospital:
          </Text>
          <OrderedList spacing={2} mb={4}>
            <ListItem>
              Has the <strong>largest number</strong> of procedures performed by
              the physician for Medicare patients; and
            </ListItem>
            <ListItem>
              Accounts for <strong>≥30%</strong> of the physician's total
              Medicare surgical volume; and
            </ListItem>
            <ListItem>
              Performed the <strong>most recent procedure</strong> in the event
              of a tie.
            </ListItem>
          </OrderedList>

          <Heading as="h3" size="base" mb={2}>
            Hospital Inclusion Criteria
          </Heading>
          <OrderedList spacing={2}>
            <ListItem>
              Must appear in Medicare inpatient or outpatient claims during the
              specified period.
            </ListItem>
            <ListItem>
              For overall hospital-level Research All-Stars across{" "}
              <strong>22</strong> specialties, a hospital must have
              <UnorderedList mt={2}>
                <ListItem>
                  At least <strong>50 physicians</strong>
                </ListItem>
                <ListItem>
                  At least <strong>10 specialties</strong>
                </ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem>
              For each specialty, hospitals with fewer than 5 attributed
              physicians are excluded.
            </ListItem>
          </OrderedList>
        </Box>

        <Divider />

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Step 3: Compile Peer-Reviewed Publications by Physicians
          </Heading>
          <Text>
            Peer-reviewed articles from the <strong>2023--2024</strong>{" "}
            <strong>PubMed</strong> <strong>database</strong> are imported for
            analysis. Physicians names are matched to author names using name
            matching and affiliation matching.
          </Text>
        </Box>

        <Divider />

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Step 4: Calculate Publication Weights
          </Heading>
          <Text mb={4}>
            Publications are assigned one point each and weighted based on{" "}
            <strong>journal impact factors</strong> and{" "}
            <strong>author positions</strong>. Letters to editors are excluded.
          </Text>

          <Heading as="h3" size="base" mb={2}>
            Weighting Criteria
          </Heading>
          <OrderedList spacing={2}>
            <ListItem>
              <strong>Journal Impact Factor</strong>: Multiplier based on the
              publication's journal.
            </ListItem>
            <ListItem>
              <strong>Author Position</strong>:
              <UnorderedList mt={2}>
                <ListItem>
                  <strong>Single author</strong>: 100%.
                </ListItem>
                <ListItem>
                  <strong>Two authors</strong>: 1st author = 60%; last author =
                  40%.
                </ListItem>
                <ListItem>
                  <strong>≥3 authors</strong>: 1st author = 45%; last author =
                  30%; other authors = 25% / (total # of authors - 2).
                </ListItem>
              </UnorderedList>
            </ListItem>
          </OrderedList>

          <Text mt={4} fontStyle="italic">
            <strong>Author-Specific Weighted Score</strong> = Journal Impact
            Factor × Author Position Weight.
          </Text>
        </Box>

        <Divider />

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Step 5: Create a Weighted Publication Score
          </Heading>
          <Text mb={4}>
            A weighted publication score is calculated for both physicians and
            hospitals.
          </Text>

          <Text mb={2}>
            <strong>Calculation Details</strong>
          </Text>
          <UnorderedList spacing={2} listStyleType="disc">
            <ListItem>
              <strong>Physician Weighted Publication Score</strong>: Sum of
              author-specific weighted article scores.
            </ListItem>
            <ListItem>
              <strong>Hospital-Level Scores</strong>:
              <UnorderedList mt={2} listStyleType="circle">
                <ListItem>
                  <strong>Specialty-Specific</strong>: Weighted average
                  publication score = Sum of attributed physician scores /
                  Number of attributed physicians.
                </ListItem>
                <ListItem>
                  <strong>Across All Specialties</strong>:
                  <UnorderedList mt={2} listStyleType="square">
                    <ListItem>
                      Normalize physician scores to a 0-100 scale for
                      comparability across specialties due to variation in
                      journal impact factors.
                    </ListItem>
                    <ListItem>
                      Hospital normalized score = Sum of normalized physician
                      scores across all specialties / Number of attributed
                      physicians.
                    </ListItem>
                  </UnorderedList>
                </ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </Box>

        <Divider my={6} />

        <Box>
          <Heading as="h2" size="md" mb={4}>
            Additional Notes
          </Heading>

          <Heading as="h3" size="base" mb={2}>
            Physician All-Stars
          </Heading>
          <OrderedList spacing={2} mb={4}>
            <ListItem>
              <strong>Imperfect Specialty Attribution</strong>: Physician
              specialties are identified using the NPI registry, which may
              contain outdated or incorrect information. This can lead to
              misclassification of specialties.
            </ListItem>
            <ListItem>
              <strong>Specialty Changes Over Time</strong>: The methodology does
              not capture physicians who change specialties during their
              careers. As such, their historical or current contributions may
              not be fully represented within the correct specialty category.
            </ListItem>
            <ListItem>
              <strong>Multiple Specialties</strong>: When physicians have
              multiple specialties in their NPI record, only the first-listed
              specialty is used for classification. This may not reflect the
              physician's primary clinical or research focus.
            </ListItem>
          </OrderedList>

          <Heading as="h3" size="base" mb={2}>
            Hospital All-Stars
          </Heading>
          <OrderedList spacing={2}>
            <ListItem>
              <strong>Exclusion of Non-Medicare Physicians</strong>: Physicians
              who have opted out of Medicare are excluded from the hospital
              attribution process because their surgical volume is not recorded
              in Medicare claims.
            </ListItem>
            <ListItem>
              <strong>Exclusion of Ambulatory Surgical Centers</strong>: The
              analysis is limited to hospital-based surgeries. Physicians
              performing surgeries exclusively in ambulatory surgical centers
              are not captured in hospital-level Research All-Stars.
            </ListItem>
          </OrderedList>
        </Box>
      </VStack>
    </Container>
  )
}

export default MethodologyContent25
